// frontend/src/pages/BulkAnalysisPage/BulkAnalysisPage.jsx

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom'; 
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
    Box, Button, CircularProgress,
    IconButton, Snackbar, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UnsavedChangesProvider } from '../../contexts/UnsavedChangesContext';
import { UIContext } from '../../contexts/UIContext';
import { HistoryContext } from '../../contexts/HistoryContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import InteractiveBatchTable from './InteractiveBatchTable';
import BatchSelector from './BatchSelector';
import DeployModal from './DeployModal';
import ConfigurationModal from './ConfigurationModal';
import LicenseModal from '../../components/AdCreativeTools/LicenseModal';
import { useAuth } from '../../contexts/AuthContext';
import './BulkAnalysisPage.css';

const BulkAnalysisPage = () => {
    const { t } = useTranslation();
    const { fetchWithAuth, user } = useAuth();
    const { isPageBlurred, setIsPageBlurred } = useContext(UIContext);
    const { setIsFaqModalOpen } = useContext(UIContext);
    const { setHasFetchedHistory } = useContext(HistoryContext);
    const [confirmedBatchDeletions, setConfirmedBatchDeletions] = useState(new Set());
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
    const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isOverwriteConfirmOpen, setIsOverwriteConfirmOpen] = useState(false);
    const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
    const [resetAction, setResetAction] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const contentRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isConfigSaving, setIsConfigSaving] = useState(false);
    const [isNewBatchJustCreated, setIsNewBatchJustCreated] = useState(false);
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [pendingLicenseItem, setPendingLicenseItem] = useState(null);
    const [licenseStatusForModal, setLicenseStatusForModal] = useState(null);
    const [isPayingLicense, setIsPayingLicense] = useState(false);
    const [isLoadingLicense, setIsLoadingLicense] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // =========================================================================
    // 2. TANSTACK QUERIES (MUST BE DECLARED BEFORE USE-EFFECTS)
    // =========================================================================

    // Add this new useEffect to sync local modals with the global context
    useEffect(() => {
        const shouldBeBlurred = isResetConfirmOpen || isSnippetModalOpen || isDeployModalOpen || isConfigModalOpen || isOverwriteConfirmOpen || isLicenseModalOpen;
        setIsPageBlurred(shouldBeBlurred);
    }, [isResetConfirmOpen, isSnippetModalOpen, isDeployModalOpen, isConfigModalOpen, isOverwriteConfirmOpen, isLicenseModalOpen, setIsPageBlurred]);

    // Query Client & Active ID state
    const queryClient = useQueryClient();
    const [activeBatchId, setActiveBatchId] = useState(null);

    // Fetch Pricing Config (Globally Cached)
    const { data: billingConfig } = useQuery({
        queryKey: ['billingConfig'],
        queryFn: async () => {
            const [priceRes, rateRes] = await Promise.all([
                fetchWithAuth('/api/config/billing'),
                fetchWithAuth('/api/user/billing?limit=1')
            ]);
            const prices = priceRes.ok ? await priceRes.json() : null;
            const rates = rateRes.ok ? await rateRes.json() : { currency_rates: { USD: 1 } };
            return { prices, rates: rates.currency_rates || { USD: 1 } };
        },
        staleTime: Infinity,
    });
    const pricingConfig = billingConfig?.prices || null;
    const currencyRates = billingConfig?.rates || { USD: 1 };

    // Fetch & Poll the Active Batch automatically
    const { data: activeBatch, isLoading: isBatchLoading } = useQuery({
        queryKey: ['batch', activeBatchId],
        queryFn: async () => {
            // We removed the manual setIsLoading() calls here!
            const response = await fetchWithAuth(`/api/bulk/batches/${activeBatchId}?poll=true`);
            if (!response.ok) {
                setNotification({ open: true, message: "Batch not found or access denied.", severity: "error" });
                navigate('/app/bulk-analysis', { replace: true });
                setActiveBatchId(null);
                throw new Error('Failed to fetch batch');
            }
            return response.json();
        },
        enabled: !!activeBatchId, 
        refetchInterval: (query) => {
            const status = query.state?.data?.status;
            return (status === 'queued' || status === 'processing') ? 5000 : false;
        }
    });

    // =========================================================================
    // 3. USE EFFECTS (These can now safely reference `activeBatch`)
    // =========================================================================

    // Scroll to the top of the batch view only when the batch ID changes.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (contentRef.current) {
                contentRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
            window.scrollTo(0, 0);
            const appWrapper = document.querySelector('.app-wrapper');
            if (appWrapper) {
                appWrapper.scrollTo(0, 0);
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [activeBatch?.batch_id]); // <-- Now it knows what activeBatch is!
    
    // Automatically open the config modal for a new batch.
    useEffect(() => {
        if (activeBatch && isNewBatchJustCreated) {
            setIsConfigModalOpen(true);
            setIsNewBatchJustCreated(false);
        }
    }, [activeBatch, isNewBatchJustCreated]);

    // Handle Deep Linking on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const deepLinkBatchId = params.get('batchId');
        if (deepLinkBatchId && !activeBatchId) {
            setActiveBatchId(deepLinkBatchId);
        }
    }, [location.search, activeBatchId]);

    // Invalidate History when a batch finishes
    useEffect(() => {
        if (activeBatch?.status === 'completed') {
            setHasFetchedHistory(false);
        }
    }, [activeBatch?.status, setHasFetchedHistory]);

    useEffect(() => {
        const shouldBeBlurred = isResetConfirmOpen || isSnippetModalOpen || isDeployModalOpen || isConfigModalOpen || isOverwriteConfirmOpen || isLicenseModalOpen;
        setIsPageBlurred(shouldBeBlurred);
    }, [isResetConfirmOpen, isSnippetModalOpen, isDeployModalOpen, isConfigModalOpen, isOverwriteConfirmOpen, isLicenseModalOpen, setIsPageBlurred]);


    // =========================================================================
    // 4. ACTION HANDLERS
    // =========================================================================

    const handleBatchUpdate = useCallback((updatedBatch) => {
        queryClient.setQueryData(['batch', updatedBatch.batch_id], updatedBatch);
    }, [queryClient]);

    const handleSelectBatch = useCallback((batch) => {
        setActiveBatchId(batch.batch_id);
        queryClient.setQueryData(['batch', batch.batch_id], batch);
        navigate(`/app/bulk-analysis?batchId=${batch.batch_id}`, { replace: true });
    }, [queryClient, navigate]);

    const handleCreateNewBatch = useCallback(async (batchName, description) => {
        try {
            const response = await fetchWithAuth('/api/bulk/batches', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ batch_name: batchName, description: description })
            });
            const responseData = await response.json();
            if (!response.ok) return { success: false, ...responseData }; 
            
            setActiveBatchId(responseData.batch_id);
            queryClient.setQueryData(['batch', responseData.batch_id], responseData);
            setIsNewBatchJustCreated(true);
            return { success: true };
        } catch (err) {
            return { success: false, message: err.message };
        }
    }, [fetchWithAuth, queryClient]);
    
    const handleBackToSelector = useCallback(() => {
        setActiveBatchId(null);
        navigate('/app/bulk-analysis', { replace: true });
    }, [navigate]);

    const handleCloseResetConfirm = () => {
        setIsResetConfirmOpen(false);
        setResetAction(null);
    };

    const handleConfirmReset = () => {
        if (resetAction) {
            resetAction();
        }
        handleCloseResetConfirm();
    };

    // --- Handler called by Table when download is clicked but unpaid ---
    const handleActivateLicenseRequest = async (item, status) => {
        // 1. Open Modal Immediately in Loading State
        setPendingLicenseItem(item);
        setLicenseStatusForModal(null); // Reset status
        setIsLoadingLicense(true);      // Start loading
        setIsLicenseModalOpen(true);    // Show modal

        // 2. Perform the check
        try {
            // Reuse the checkLicenseStatus helper logic or fetch directly
            const response = await fetchWithAuth(`/api/verify/all/${item.exactHash}`);
            if (response.ok) {
                const data = await response.json();
                const info = data.license_info;
                
                // Logic: If inactive/expired, user pays. If active, they see "Active" state.
                // The Modal will handle showing the "Download" button if active.
                if (info && info.status === 'inactive' && !info.expires_at) {
                     setLicenseStatusForModal(null); // New asset
                } else {
                     setLicenseStatusForModal(info);
                }
            }
        } catch (e) {
            console.error("License check failed", e);
            // Optionally set an error state here
        } finally {
            // 3. Stop loading, allowing the Modal to render the form or the download button
            setIsLoadingLicense(false);
        }
    };

    // --- Payment Logic (Copied/Adapted from VerificationPage) ---
    const handleLicensePayment = async (durationDays, costUnits) => {
        if (!pendingLicenseItem) return;
        
        // setIsLicenseModalOpen(false);
        setIsPayingLicense(true);
        try {
            const response = await fetchWithAuth('/api/license/activate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    exact_hash: pendingLicenseItem.exactHash,
                    content_type: pendingLicenseItem.contentType,
                    duration_days: durationDays,
                    context_hash: pendingLicenseItem.contextHash
                })
            });
            
            if (!response.ok) throw new Error("Activation failed");

            // Success!
            setNotification({ 
                open: true, 
                message: t('adCreativeTools.notifications.licenseActiveSuccess', "License activated successfully! Downloading..."), 
                severity: 'success' 
            });
            
            // --- AUTOMATIC DOWNLOAD TRIGGER ---
            // 1. Capture the ID we need
            const itemIdToDownload = pendingLicenseItem.id;

            // 2. Close the modal
            setIsLicenseModalOpen(false);
            setPendingLicenseItem(null); // Clear the pending ref
            setIsPayingLicense(false);   // Stop spinner

            // 3. Trigger the table to start downloading (skipping license check)
            // We use a small timeout to ensure the modal closing animation doesn't conflict with state updates
            setTimeout(() => {
                setTriggerDownloadItemId(itemIdToDownload);
            }, 100);

        } catch (e) {
            console.error("Payment failed", e);
            setNotification({ 
                open: true, 
                message: "Payment failed. Please try again.", 
                severity: 'error' 
            });
        } finally {
            setIsPayingLicense(false);
        }
    };

    // --- Download Handler (Passed to Modal) ---
    const handleModalDownload = async () => {

        const itemIdToDownload = pendingLicenseItem?.id;
         // Close the modal
         setIsLicenseModalOpen(false);
         setPendingLicenseItem(null);
         

         // Trigger download immediately
         if (itemIdToDownload) {
             setTriggerDownloadItemId(itemIdToDownload);
         }
    };

    const [triggerDownloadItemId, setTriggerDownloadItemId] = useState(null);

    return (
        <UnsavedChangesProvider> 
            <div className="bulk-analysis-page-wrapper">
                <div className="bulk-page-content-wrapper" ref={contentRef}>
                    <Helmet>
                        <title>{t('bulkAnalysisPage.helmet.title')}</title>
                    </Helmet>
                    <Header showDataFlowPreview={false} showBackButton={true} />
                    {/* The content wrapper now IS the main content area */}
                    <main className={`bulk-main-content ${isPageBlurred ? 'app-content-blurred' : ''}`}>
                        <header className="bulk-header-section">
                            <h1>{t('bulkAnalysisPage.title')}</h1>
                            <p className="bulk-page-description">
                                {t('bulkAnalysisPage.description')}
                            </p>
                        </header>
                        {isBatchLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : activeBatch ? (
                            <div className="batch-view-container">
                                <InteractiveBatchTable
                                    key={activeBatch.batch_id}
                                    initialBatch={activeBatch}
                                    onBack={handleBackToSelector}
                                    // When this is called, we set local state to open the modal
                                    onOpenResetConfirm={(action) => {
                                        setResetAction(() => action);
                                        setIsResetConfirmOpen(true);
                                    }}
                                    setNotification={setNotification}
                                    onBatchUpdate={handleBatchUpdate}
                                    confirmedBatchDeletions={confirmedBatchDeletions}
                                    setConfirmedBatchDeletions={setConfirmedBatchDeletions}
                                    setIsSnippetModalOpen={setIsSnippetModalOpen}
                                    isDeployModalOpen={isDeployModalOpen}
                                    setIsDeployModalOpen={setIsDeployModalOpen}
                                    setIsConfigModalOpen={setIsConfigModalOpen}
                                    isExporting={isExporting}
                                    isOverwriteConfirmOpen={isOverwriteConfirmOpen}
                                    setIsOverwriteConfirmOpen={setIsOverwriteConfirmOpen}
                                    onActivateLicense={handleActivateLicenseRequest}
                                    triggerDownloadItemId={triggerDownloadItemId}
                                    onDownloadTriggered={() => setTriggerDownloadItemId(null)}
                                />
                            </div>
                        ) : (
                            <BatchSelector
                                onSelectBatch={handleSelectBatch}
                                onCreateNew={handleCreateNewBatch}
                                setNotification={setNotification}
                            />
                        )}
                    </main>
                </div>
                <Footer 
                    onOpenFaqModal={() => setIsFaqModalOpen(true)} 
                    showFaqButton={true} 
                />
            </div>
                
            {/* --- NOTIFICATIONS SNACKBAR --- */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%' }} variant="filled">
                    {notification.message}
                </Alert>
            </Snackbar>

            {/* --- MODALS (RENDERED VIA PORTALS TO ESCAPE BLUR) --- */}

            {/* 1. RESET CONFIRMATION DIALOG */}
            {ReactDOM.createPortal(
                <div 
                    className={`confirmation-dialog-overlay ${isResetConfirmOpen ? 'open' : ''}`} 
                    onClick={handleCloseResetConfirm} 
                    role="dialog" 
                    aria-modal="true"
                >
                    {isResetConfirmOpen && (
                        <div className="confirmation-dialog-content" onClick={(e) => e.stopPropagation()}>
                            <IconButton aria-label="close" onClick={handleCloseResetConfirm} className="dialog-close-button">
                                <CloseIcon />
                            </IconButton>
                            <h2 className="confirmation-dialog-title">{t('bulkAnalysisPage.table.resetConfirm.title')}</h2>
                            <div className="confirmation-dialog-body">
                                <p className="confirmation-dialog-text">{t('bulkAnalysisPage.table.resetConfirm.description')}</p>
                            </div>
                            <div className="confirmation-dialog-actions">
                                <Button
                                    onClick={handleCloseResetConfirm}
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                        color: 'var(--secondary-color)',
                                        '&:hover': {
                                            borderColor: 'var(--secondary-color)',
                                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                            color: 'var(--secondary-color)',
                                        }
                                    }}
                                >
                                    {t('bulkAnalysisPage.table.resetConfirm.cancelButton')}
                                </Button>
                                <Button
                                    onClick={handleConfirmReset}
                                    color="error"
                                    variant="contained"
                                    autoFocus
                                >
                                    {t('bulkAnalysisPage.table.resetConfirm.confirmButton')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>,
                document.body // Attach to body to be outside the blurred div
            )}

            {/* 2. DEPLOY MODAL */}
            {activeBatch && (
                <DeployModal
                    isOpen={isDeployModalOpen}
                    onClose={() => setIsDeployModalOpen(false)}
                    isExporting={isExporting}
                    onDownloadDcm={async () => {
                        setIsDeployModalOpen(false); 
                        setIsExporting(true);
                        try {
                            const response = await fetchWithAuth(`/api/bulk/batches/${activeBatch.batch_id}/export?platform=dcm`);

                            if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.error || t('bulkAnalysisPage.notifications.downloadCsvFailed'));
                            }
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            
                            // This now uses the backend's Content-Disposition header for the filename
                            const contentDisposition = response.headers.get('content-disposition');
                            let downloadFilename = 'dcm_export_verified.csv'; // Fallback
                            if (contentDisposition) {
                                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                                if (filenameMatch && filenameMatch.length > 1) {
                                    downloadFilename = filenameMatch[1];
                                }
                            }
                            a.download = downloadFilename;

                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                        } catch (err) {
                            setNotification({ open: true, message: err.message, severity: 'error' });
                        } finally {
                            setIsExporting(false);
                        }
                    }}
                />
            )}

            {activeBatch && (
                <ConfigurationModal
                    isOpen={isConfigModalOpen}
                    onClose={() => setIsConfigModalOpen(false)}
                    currentMode={activeBatch.batch_mode}
                    isSaving={isConfigSaving} 
                    onSave={async (newMode) => {
                        setIsConfigSaving(true);
                        try {
                            const response = await fetchWithAuth(`/api/bulk/batches/${activeBatch.batch_id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ batch_mode: newMode })
                            });
                            if (!response.ok) throw new Error(t('bulkAnalysisPage.notifications.configSaveFailed'));
                            const updatedBatch = await response.json();
                            handleBatchUpdate(updatedBatch);
                            setNotification({ open: true, message: t('bulkAnalysisPage.notifications.configSaveSuccess'), severity: 'success' });
                            setIsConfigModalOpen(false);
                        } catch (err) {
                            setNotification({ open: true, message: err.message, severity: 'error' });
                        } finally {
                            setIsConfigSaving(false);
                        }
                    }}
                />
            )}

            {/* --- LICENSE MODAL --- */}
            {pendingLicenseItem && (
                <LicenseModal 
                    isOpen={isLicenseModalOpen}
                    onClose={() => setIsLicenseModalOpen(false)}
                    onConfirm={handleLicensePayment}
                    onDownload={handleModalDownload}
                    isPaying={isPayingLicense} 
                    isLoading={isLoadingLicense}
                    currentLicense={licenseStatusForModal}
                    contentType={pendingLicenseItem.contentType}
                    pricingConfig={pricingConfig}
                    currencyRates={currencyRates}
                    selectedCurrency={user?.preferred_currency || 'USD'}
                    mode={licenseStatusForModal?.status === 'active' ? 'download' : 'activate'}
                />
            )}

        </UnsavedChangesProvider>
    );
};

export default BulkAnalysisPage;